import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../../services/apiClient';
import type { ApiResponse } from '../../../types/api';
import Spinner from '../../../components/ui/Spinner';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const SupplierFormPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const isEdit = !!editId;

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [form, setForm] = useState({ rnc: '', name: '', commercial_name: '', phone: '', email: '' });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        if (!editId) return;
        apiClient.get<ApiResponse<any>>(`/suppliers/${editId}`).then(res => {
            const s = res.data.data;
            setForm({ rnc: s.rnc, name: s.name, commercial_name: s.commercial_name || '', phone: s.phone || '', email: s.email || '' });
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [editId]);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.rnc.trim()) e.rnc = 'Requerido';
        if (!form.name.trim()) e.name = 'Requerido';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitError(null);
        setSaving(true);
        try {
            if (isEdit) {
                await apiClient.put(`/suppliers/${editId}`, form);
            } else {
                await apiClient.post('/suppliers', form);
            }
            navigate('/suppliers');
        } catch { setSubmitError('Error al guardar'); }
        finally { setSaving(false); }
    };

    if (loading) return <Spinner variant="full-page" text="Cargando..." size="lg" />;

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <div className="page-header">
                    <div className="row">
                        <div className="col-sm-12">
                            <Breadcrumb items={[{ label: 'Inicio', to: '/admin-dashboard' }, { label: 'Proveedores', to: '/suppliers' }, { label: isEdit ? 'Editar' : 'Nuevo' }]} />
                        </div>
                    </div>
                    <h4 className="page-title">{isEdit ? 'Editar' : 'Nuevo'} Proveedor</h4>
                </div>
                <div className="card"><div className="card-body">
                    {submitError && <div className="alert alert-danger">{submitError}</div>}
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>RNC *</label>
                                    <input className={`form-control ${errors.rnc ? 'is-invalid' : ''}`}
                                        value={form.rnc} maxLength={11}
                                        onChange={e => setForm(p => ({...p, rnc: e.target.value}))} />
                                    {errors.rnc && <div className="invalid-feedback">{errors.rnc}</div>}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>Nombre *</label>
                                    <input className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        value={form.name} maxLength={200}
                                        onChange={e => setForm(p => ({...p, name: e.target.value}))} />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group"><label>Nombre Comercial</label>
                                    <input className="form-control" value={form.commercial_name} maxLength={200}
                                        onChange={e => setForm(p => ({...p, commercial_name: e.target.value}))} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group"><label>Teléfono</label>
                                    <input className="form-control" value={form.phone} maxLength={50}
                                        onChange={e => setForm(p => ({...p, phone: e.target.value}))} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group"><label>Email</label>
                                    <input className="form-control" value={form.email} maxLength={100}
                                        onChange={e => setForm(p => ({...p, email: e.target.value}))} />
                                </div>
                            </div>
                        </div>
                        <div className="text-end mt-3">
                            <Link to="/suppliers" className="btn btn-secondary me-2">Cancelar</Link>
                            <button className="btn btn-primary" disabled={saving}>
                                {saving ? <Spinner size="sm" text="Guardando..." /> : 'Guardar'}
                            </button>
                        </div>
                    </form>
                </div></div>
            </div>
        </div>
    );
};

export default SupplierFormPage;
